import { containers, elements, feedback, layouts } from '@ecosystem/newron-design-system';
import React, { useEffect, useMemo, useState } from 'react';
import { Link } from 'react-router-dom';
import { useSelector } from 'react-redux';
import {
  initialProductsCompareKpi,
  ProductsCompareExport,
  ProductsCompareKpi,
  RetailerKpiOption,
  SaleType,
  TrendsKpi,
} from '@models/Products';
import {
  countriesToQuery,
  getCurrentRetailerClientState,
  loadingDataPreparation,
  PRODUCTS_YTD_MAT_WARNING_MSG,
  ProductsParamArgumentObject,
} from '../product-analyzer/Product';
import { getProductsCompare, getProductsCompareExport } from '@services/productTab/ProductsCompare.service';
import saveErrorLog, { ErrorTagEnum } from '@utils/LogUtil';
import { usePageConfig } from '@utils/pageConfig/PagesConfigContext';
import { Country, FilterKeys, Option, PageName, PageType } from '@utils/pageConfig/Types';
import { constructProductsParam } from '@utils/serviceUtils/RequestFormatter';
import { RelativePeriod } from '@components/comparison-period-selector/Types';
import getComparisonPeriod, { PeriodFormat } from '@components/filter-selection-note/GetComparisonPeriod';
import FilteredPeriod from '@components/filtered-period/FilteredPeriod';
import ProductsMarketCard from '@pages/product-analyzer/trends/products-market-card/ProductsMarketCard';
import ProductsRetailerCard from '@pages/product-analyzer/trends/products-retailer-card/ProductsRetailerCard';
import { FocusArea, KpiOptionForValue } from '@components/products-top-5-table/Types';
import ProductsCompareModalTable from '@components/products-compare-modal/products-compare-modal-table/ProductsCompareModalTable';
import * as styles from '@components/products-compare-modal/ProductsCompareModal.module.scss';
import { TOTAL_ITEM_NAME } from '@components/products-compare-modal/Types';
import {
  getAppliedFiltersFromPagesConfig,
  getFiltersInitialState,
  getInitialAppliedCountries,
} from '@utils/FilterUtils';
import { checkPeriodExist, isPeriodYTDOrMAT } from '@utils/PeriodUtils';
import {
  useProductsCompareExport,
  UseProductsCompareExportExcelParam,
} from '@components/products-compare-modal/UseProductsCompareExport';
import { IRootState } from '@state/reducers/Reducers';
import { getEffectiveProductType } from '@models/SubscribedProductType';
import useCompareProductsState from './UseCompareProductsState';
import {
  PAGE_VIEW_TRACKING_TITLE,
  pageViewTrackingFunction,
  PageViewTrackingFunctionParam,
} from '@utils/page-analytics/model/PageViewedTracking';
import { useAnalytics } from '@ecosystem/newron-client-lib';
import {
  PAGE_ELEMENT_CATEGORY_CTA,
  PAGE_ELEMENT_CLICK_TRACKING_TITLE,
  PAGE_ELEMENT_TYPE,
  pageElementClickTrackingFunction,
  PageElementClickTrackingParam,
} from '@utils/page-analytics/model/PageElementClickedTracking';
import useDebouncedEffect from '@hooks/UseDebouncedEffect';
import { useExportImages } from '@hooks/export/UseExportChartImage';
import { WithAlert } from '@components/design';
import ProductsChartsFilter from '@components/products-line-charts/products-charts-filter/ProductsChartsFilter';
import { BASE_RETAIL_PATH, RETAIL_ALLOWED_PATH } from '@utils/UrlUtils';
import { setApiErrorState } from '@services/utils/ErrorHandlers';

const { Icon } = elements;
const { ArrowBackward, HelpOutline } = Icon.icons;
const { Page } = layouts;
const { Card } = containers;

const TooltipContent = () => {
  return (
    <>
      <p>Select a metric to see data around market performance (Revenue / Units) or Price, trended over 13 periods.</p>
      <p>
        If you select market performance (Revenue / Units), you can then choose to look at your Share of each
        product&apos;s overall market sales, or Mix, which details each product&apos;s share of your overall sales in
        the category.
      </p>
    </>
  );
};

const ProductsCompare: React.FC = () => {
  const { clearExportImage } = useExportImages(PageType.RI_PRODUCT);

  const { timeFrame, factRevenue, currentProductType, relativePeriod, selectedPeriod, latestPeriod, selectedModels } =
    useCompareProductsState();

  const { track } = useAnalytics();
  const sideFiltersState = useSelector((state: IRootState) => state.sideFilters);

  const trackPageView = () => {
    const trackingParam: PageViewTrackingFunctionParam = {
      eventTitle: PAGE_VIEW_TRACKING_TITLE,
      sideFilters: sideFiltersState.appliedFilters,
      retailerClientState,
      track,
      pageType: PageType.RI_PRODUCT,
      pageName: PageName.RI_PRODUCT_COMPARE,
      access: pagesConfig.access,
      initialLocationId: getInitialAppliedCountries(pagesConfig, PageType.RI_PRODUCT, effectiveProductType),
    };

    pageViewTrackingFunction(trackingParam);
  };

  const [selectedLineChartSaleType, setSelectedLineChartSaleType] = useState<SaleType>(SaleType.revenue);
  const [selectedRetailerOption, setSelectedRetailerOption] = useState<RetailerKpiOption>(RetailerKpiOption.absolute);
  const [isLoading, setIsLoading] = useState<boolean>(false);

  const [isApiError, setIsApiError] = useState<boolean>(false);
  const [data, setData] = useState<ProductsCompareKpi>(initialProductsCompareKpi);
  const [exportData, setExportData] = useState<ProductsCompareExport[]>([]);
  const pagesConfig = usePageConfig();

  const subscribedProductTypes = useSelector(
    (state: IRootState) => state.subscribedProductTypes.subscribedProductTypes,
  );
  const effectiveProductType = getEffectiveProductType(subscribedProductTypes, PageName.RI_PRODUCT);

  const retailerClientState = useSelector((state: IRootState) => state.retailerClient);
  const hitlistsRetailerClientState = useSelector((state: IRootState) => state.hitlistsRetailerClient);
  const sellerInsightsRetailerClientState = useSelector((state: IRootState) => state.sellerInsightsRetailerClient);
  const currentRetailerClientState = getCurrentRetailerClientState(
    currentProductType,
    retailerClientState,
    hitlistsRetailerClientState,
    sellerInsightsRetailerClientState,
  );

  const mainFilterStateProducts = useSelector((state: any) => state.mainFiltersProducts);
  const initialFiltersProducts = useSelector((state: any) => state.initialFiltersProducts);

  //disable export button while the data is still loading/inline alert is displaying
  const [isExportButtonDisabled, setIsExportButtonDisabled] = useState<boolean>(false);
  const [leaderboardUpdate, setLeaderboardUpdate] = useState<boolean>(false);
  const loadData = async (_: any, countriesToQueryObject: Country[], pg: Option) => {
    try {
      setIsLoading(true);
      setIsExportButtonDisabled(true);
      setIsApiError(false);
      setIsApiError(false);

      const productsParamObject: ProductsParamArgumentObject = {
        productType: effectiveProductType,
        pageNumber: 0,
        productGroupParam: pg,
        pagesConfig,
        period: selectedPeriod,
        mainFilterStateProducts,
        retailerClientState: currentRetailerClientState,
        countriesToQuery: countriesToQueryObject,
        timeFrame,
        defaultPageSize: 0,
        selectedSaleType: selectedLineChartSaleType,
        sortingKpi: KpiOptionForValue.bestSellers,
        isMarket: FocusArea.market,
        growthType: undefined,
        itemIdList: selectedModels,
        comparisonPeriod: relativePeriod,
      };
      const productsParam = constructProductsParam(productsParamObject);
      const productsParamExport = {
        ...productsParam!,
        exportCompareComparisonType: relativePeriod,
      };

      const [productsCompareData, productsCompareExportData] = await Promise.all([
        getProductsCompare(productsParam!),
        getProductsCompareExport(productsParamExport),
      ]);

      setData(productsCompareData);
      setExportData(productsCompareExportData);
      setIsExportButtonDisabled(false);
      setLeaderboardUpdate(true);
    } catch (err: any) {
      console.error(err);
      saveErrorLog(err, 'Products Compare Model - Unable to get products compare model api response.', {
        category: ErrorTagEnum.ApiError,
        requestPayload: {
          clientId: currentRetailerClientState.clientId,
          productGroup: pg,
          period: selectedPeriod.value,
          comparisonPeriod: RelativePeriod.PreviousYearCurrentPeriod,
        },
      });
      //Set back to initial state, to show no result if error occur.
      setData(initialProductsCompareKpi);
      setExportData([]);
      setIsExportButtonDisabled(true);
      setLeaderboardUpdate(false);
      setApiErrorState(err, setIsApiError);
    } finally {
      setIsLoading(false);
      clearExportImage();
    }
  };

  const handleElementClickTracking = () => {
    const params: PageElementClickTrackingParam = {
      eventTitle: PAGE_ELEMENT_CLICK_TRACKING_TITLE,
      sideFilters: sideFiltersState.appliedFilters,
      retailerClientState: retailerClientState,
      track: track,
      pageType: PageType.RI_PRODUCT,
      pageName: PageName.RI_PRODUCT,
      elementName: 'Back to leaderboard',
      access: pagesConfig.access,
      elementCategory: PAGE_ELEMENT_CATEGORY_CTA,
      elementType: PAGE_ELEMENT_TYPE,
    };
    pageElementClickTrackingFunction(params);
  };

  useEffect(() => {
    if (selectedModels.length) {
      loadingDataPreparation(
        pagesConfig,
        isLoading,
        initialFiltersProducts.initialFilters,
        timeFrame,
        loadData,
        effectiveProductType,
      );
    }
    pagesConfig.toggleFiltersTray(false);
  }, [selectedModels.length]);

  useDebouncedEffect(() => {
    if (selectedModels.length) {
      trackPageView();
    }
  }, [selectedModels]);

  const comparisonPeriod = getComparisonPeriod(selectedPeriod, timeFrame, relativePeriod, PeriodFormat.LONG);

  const dataWithoutTotal: ProductsCompareKpi = {
    ...data,
    items: data.items.filter((item) => item.item.itemName !== TOTAL_ITEM_NAME),
  };

  const productGroup = useMemo(
    () => getAppliedFiltersFromPagesConfig(pagesConfig, FilterKeys.PRODUCT_GROUP, PageType.RI_PRODUCT),
    [pagesConfig],
  );

  const countries = useMemo(
    () => getAppliedFiltersFromPagesConfig(pagesConfig, FilterKeys.COUNTRY, PageType.RI_PRODUCT),
    [pagesConfig],
  );

  const responseTimeFrame = checkPeriodExist(
    initialFiltersProducts.initialFilters,
    countriesToQuery(
      pagesConfig,
      getInitialAppliedCountries(pagesConfig, PageType.RI_PRODUCT, currentProductType),
      currentProductType,
    )[0]?.id,
    mainFilterStateProducts.timeFrame,
  );
  const productsParamObjectExport: ProductsParamArgumentObject = {
    productType: effectiveProductType,
    pageNumber: 0,
    productGroupParam: productGroup ? productGroup[0] : [],
    pagesConfig: pagesConfig,
    period: selectedPeriod,
    mainFilterStateProducts: mainFilterStateProducts,
    retailerClientState: currentRetailerClientState,
    countriesToQuery: countriesToQuery(pagesConfig, countries ?? [], currentProductType),
    timeFrame: responseTimeFrame,
    defaultPageSize: 50,
    selectedSaleType: selectedLineChartSaleType,
    sortingKpi: undefined,
    isMarket: undefined,
    growthType: mainFilterStateProducts.selectedGrowthType,
    overrideKpi: selectedLineChartSaleType,
    comparisonPeriod: relativePeriod,
  };

  //Export Functionalities for products compare modal
  const useProductsCompareParam: UseProductsCompareExportExcelParam = {
    productsParam: constructProductsParam(productsParamObjectExport),
    mainFilterStateProducts: mainFilterStateProducts,
    productsCompareExport: exportData,
    pagesConfig: pagesConfig,
    appliedCountries: getInitialAppliedCountries(pagesConfig, PageType.RI_PRODUCT, currentProductType),
    appliedPg: getFiltersInitialState(
      pagesConfig.appliedFilters[PageType.RI_PRODUCT].selectedFilters,
      FilterKeys.PRODUCT_GROUP,
    )?.options,
    factRevenue,
    timeFrame: timeFrame,
    leaderboardUpdate: leaderboardUpdate,
    featureData: data,
    selectedSalesType: selectedLineChartSaleType,
    selectedRetailerOption: selectedRetailerOption,
    comparisonPeriod: relativePeriod,
    latestPeriod,
    trendsKpi: dataWithoutTotal,
  };

  const onChartDownload = useProductsCompareExport(useProductsCompareParam);

  const exportExcelParam = {
    isExportButtonDisabled: isExportButtonDisabled,
    onChartDownload: onChartDownload,
  };

  const { Tooltip, ActionType } = feedback;
  return (
    <Page>
      <Page.Title>
        <div className="pt-6">
          <button type="button" className="inline-flex" onClick={() => handleElementClickTracking()}>
            <Link to={`/${BASE_RETAIL_PATH}/${RETAIL_ALLOWED_PATH.PRODUCTS_LEADERBOARD}`} className="inline-flex">
              <div className="flex flex-row items-center">
                <div className="mr-1">
                  <Icon size="sm" color="brand">
                    <ArrowBackward />
                  </Icon>
                </div>
                <div>Back to leaderboard</div>
              </div>
            </Link>
          </button>
        </div>

        <h1 className={styles.TradingAssistant__Title}>
          <span>Compare Models</span>
        </h1>
        <FilteredPeriod
          selectedPeriod={selectedPeriod.label}
          comparisonPeriod={comparisonPeriod}
          selectedPeriodicity={timeFrame}
          isDisplayNoComparisonLabel={isPeriodYTDOrMAT(selectedPeriod)}
        />
      </Page.Title>
      {isLoading ? (
        <feedback.DotsLoader />
      ) : (
        <Page.Content>
          {isPeriodYTDOrMAT(selectedPeriod) && (
            <div className="my-4" data-testid="products-ytd-mat-warning-msg">
              <WithAlert title={PRODUCTS_YTD_MAT_WARNING_MSG} actionType={ActionType.DEFAULT} />
            </div>
          )}
          <div className="grid grid-rows-1 grid-cols-1 pt-6">
            <Card>
              <ProductsChartsFilter
                selectedLineChartSaleType={selectedLineChartSaleType}
                setSelectedLineChartSaleType={setSelectedLineChartSaleType}
                selectedShareOption={selectedRetailerOption}
                setSelectedShareOption={setSelectedRetailerOption}
                factRevenue={factRevenue}
                isLoading={isLoading}
                tooltipComponent={
                  <div className={styles.ProductsCompareModal__Tooltip}>
                    <Tooltip testId="products-compare-modal-tooltip" placement="bottom" theme="light">
                      <Tooltip.Content>
                        <TooltipContent />
                      </Tooltip.Content>
                      <div>
                        <Icon size="md" color="brand">
                          <HelpOutline />
                        </Icon>
                      </div>
                    </Tooltip>
                  </div>
                }
                retailerExportExcelParam={exportExcelParam}
              />
              <div
                key={`${selectedLineChartSaleType}`}
                className={`${styles.ProductsCompareModal__Content_Cards} products-compare-line-chart-wrapper`}
              >
                <div className={styles.ProductsCompareModal__Content_Card}>
                  <ProductsMarketCard
                    trendsKpi={dataWithoutTotal as unknown as TrendsKpi}
                    selectedPeriodicity={timeFrame}
                    selectedLineChartSaleType={selectedLineChartSaleType}
                    setSelectedLineChartSaleType={setSelectedLineChartSaleType}
                    factRevenue={factRevenue}
                    isLoading={isLoading}
                    excludeFilter={true}
                    excludeTooltipIcon={true}
                    isApiError={isApiError}
                  />
                </div>
                <div className={styles.ProductsCompareModal__Content_Card}>
                  <ProductsRetailerCard
                    trendsKpi={dataWithoutTotal as unknown as TrendsKpi}
                    selectedPeriodicity={timeFrame}
                    selectedLineChartSaleType={selectedLineChartSaleType}
                    setSelectedLineChartSaleType={setSelectedLineChartSaleType}
                    factRevenue={factRevenue}
                    isLoading={isLoading}
                    selectedOption={selectedRetailerOption}
                    setSelectedOption={setSelectedRetailerOption}
                    excludeTooltipIcon={true}
                  />
                </div>
              </div>
            </Card>
            <ProductsCompareModalTable
              productsCompareKpi={data}
              isLoading={isLoading}
              currency={factRevenue.currencySymbol}
              isApiError={isApiError}
            />
          </div>
        </Page.Content>
      )}
    </Page>
  );
};

export default ProductsCompare;
